/* Progressive enhancement. Shopify remains the source of prices and cart totals. */
(() => {
  'use strict';
  document.documentElement.classList.add('js');
  document.documentElement.classList.remove('no-js');
  const config = () => window.AtelierTheme || {};
  const dialogOpeners = new WeakMap();

  function closeMenus(except) {
    document.querySelectorAll('.mobile-menu[open], .nav-dropdown[open]').forEach(menu => {
      if (menu !== except) menu.open = false;
    });
  }

  function showDialog(dialog, trigger) {
    if (!dialog || typeof dialog.showModal !== 'function') return false;
    closeMenus();
    document.querySelectorAll('dialog[open]').forEach(other => {
      if (other !== dialog) other.close();
    });
    dialogOpeners.set(dialog, trigger || document.activeElement);
    if (!dialog.open) dialog.showModal();
    return true;
  }

  function updateGalleryActive(gallery, target) {
    const media = [...gallery.querySelectorAll('.product-media')];
    const index = media.indexOf(target);
    if (index < 0) return;
    gallery.dataset.activeMediaId = target.dataset.mediaId || '';
    media.forEach(item => {
      if (item !== target) item.querySelectorAll('video').forEach(video => video.pause());
    });
    gallery.querySelectorAll('[data-media-link]').forEach(link => {
      if (link.getAttribute('href') === '#' + target.id) link.setAttribute('aria-current', 'true');
      else link.removeAttribute('aria-current');
    });
    const previous = gallery.querySelector('[data-gallery-prev]');
    const next = gallery.querySelector('[data-gallery-next]');
    if (previous) previous.disabled = index === 0;
    if (next) next.disabled = index === media.length - 1;
  }

  function setGalleryMedia(gallery, target, focus = false, instant = false) {
    const viewport = gallery.querySelector('[data-gallery-viewport]');
    if (!viewport || !target) return;
    updateGalleryActive(gallery, target);
    const first = gallery.querySelector('.product-media');
    const left = target.offsetLeft - (first?.offsetLeft || 0);
    if (instant || window.matchMedia('(prefers-reduced-motion: reduce)').matches) viewport.scrollLeft = left;
    else viewport.scrollTo({left, behavior: 'smooth'});
    if (focus) target.focus({preventScroll: true});
  }

  function moveGallery(gallery, direction) {
    const media = [...gallery.querySelectorAll('.product-media')];
    if (media.length < 2) return;
    const current = media.findIndex(item => item.dataset.mediaId === gallery.dataset.activeMediaId);
    const next = Math.min(media.length - 1, Math.max(0, Math.max(current, 0) + direction));
    if (next !== current) setGalleryMedia(gallery, media[next]);
  }

  function money(amount, currency) {
    return new Intl.NumberFormat(document.documentElement.lang || 'en', {
      style: 'currency', currency: currency || 'USD'
    }).format(amount / 100);
  }

  function selectProductOption(link) {
    const section = link.closest('[data-product-section]');
    const variantField = section?.querySelector('form[data-ajax-add] [name="id"]');
    const variantData = section?.querySelector('[data-product-variants]');
    if (!section || !variantField || !variantData) return false;
    let variants;
    try { variants = JSON.parse(variantData.textContent); }
    catch { return false; }
    const fields = [...section.querySelectorAll('.variant-groups .option-field')];
    const choices = fields.map(field => {
      const chosen = field.contains(link) ? link : field.querySelector('[data-option-link][aria-current="true"]');
      return chosen?.dataset.optionValue || chosen?.textContent.trim();
    });
    const variant = variants.find(item => item.options?.every((value, index) => value === choices[index]));
    if (!variant) return false;
    fields.forEach(field => {
      const chosen = field.contains(link) ? link : field.querySelector('[data-option-link][aria-current="true"]');
      field.querySelectorAll('[data-option-link]').forEach(item => item.setAttribute('aria-current', String(item === chosen)));
      const label = field.querySelector('.option-label span');
      if (label && chosen) label.textContent = chosen.dataset.optionLabel || chosen.textContent.trim();
    });
    const oldVariant = variants.find(item => String(item.id) === variantField.value);
    const oldPrice = Number((section.querySelector('[data-price-current]')?.textContent || '').replace(/[^\d.,]/g, '').replace(/,/g, '')) * 100;
    const planRatio = oldVariant?.price && oldPrice > 0 ? Math.min(1, oldPrice / oldVariant.price) : 1;
    variantField.value = variant.id;
    variantField.disabled = !variant.available;
    section.querySelectorAll('.purchase-actions button[type="submit"]').forEach(button => button.disabled = !variant.available);
    const price = section.querySelector('[data-product-price]');
    const current = price?.querySelector('[data-price-current]');
    const compare = price?.querySelector('[data-price-compare]');
    if (current) current.textContent = money(Math.round(variant.price * planRatio), section.dataset.currency);
    if (compare) {
      const comparePrice = planRatio < .999 ? variant.price : variant.compare_at_price;
      compare.textContent = comparePrice > variant.price * planRatio ? money(comparePrice, section.dataset.currency) : '';
      compare.hidden = !(comparePrice > variant.price * planRatio);
    }
    const gallery = section.querySelector('[data-gallery]');
    let featuredMediaId = variant.featured_media?.id || variant.featured_image?.id;
    try {
      const mediaMap = JSON.parse(section.querySelector('[data-variant-media]')?.textContent || '{}');
      featuredMediaId = mediaMap[String(variant.id)] || featuredMediaId;
    } catch { /* Keep the variant's own image when media mapping is unavailable. */ }
    const media = featuredMediaId && gallery?.querySelector(`[data-media-id="${featuredMediaId}"]`);
    if (gallery && media) setGalleryMedia(gallery, media);
    else if (gallery?.hasAttribute('data-carousel')) {
      const first = gallery.querySelector('.product-media');
      if (first) setGalleryMedia(gallery, first);
    }
    const destination = new URL(link.href);
    const sellingPlan = new FormData(variantField.form).get('selling_plan');
    if (sellingPlan) destination.searchParams.set('selling_plan', sellingPlan);
    destination.hash = '';
    history.replaceState({ variantId: variant.id }, '', destination);
    variantField.dispatchEvent(new Event('change', { bubbles: true }));
    document.dispatchEvent(new CustomEvent('variant:change', { detail: { variant, section } }));
    return true;
  }

  function initialize(scope = document) {
    scope.querySelectorAll('[data-gallery]').forEach(gallery => {
      if (gallery.dataset.ready) return;
      gallery.dataset.ready = 'true';
      const media = [...gallery.querySelectorAll('.product-media')];
      const active = media.find(item => item.dataset.mediaId === gallery.dataset.featuredMedia) || media[0];
      if (active) setGalleryMedia(gallery, active, false, true);
      if (gallery.hasAttribute('data-carousel')) {
        const viewport = gallery.querySelector('[data-gallery-viewport]');
        let scrollFrame = 0;
        viewport?.addEventListener('scroll', () => {
          if (scrollFrame) return;
          scrollFrame = requestAnimationFrame(() => {
            scrollFrame = 0;
            const current = Math.round(viewport.scrollLeft / Math.max(1, viewport.clientWidth));
            if (media[current]) updateGalleryActive(gallery, media[current]);
          });
        }, {passive: true});
      }
    });
    scope.querySelectorAll('dialog').forEach(dialog => {
      if (dialog.dataset.ready) return;
      dialog.dataset.ready = 'true';
      dialog.addEventListener('close', () => {
        const opener = dialogOpeners.get(dialog);
        if (opener?.isConnected) opener.focus();
      });
    });
    scope.querySelectorAll('.mobile-menu, .nav-dropdown').forEach(menu => {
      if (menu.dataset.ready) return;
      menu.dataset.ready = 'true';
      const summary = menu.querySelector('summary');
      summary?.setAttribute('aria-expanded', String(menu.open));
      menu.addEventListener('toggle', () => {
        summary?.setAttribute('aria-expanded', String(menu.open));
        if (menu.open) closeMenus(menu);
      });
    });
  }

  document.addEventListener('click', event => {
    if (!(event.target instanceof Element)) return;
    const close = event.target.closest('[data-dialog-close]');
    if (close) { close.closest('dialog')?.close(); return; }
    const search = event.target.closest('[data-open-search]');
    if (search && showDialog(document.querySelector('[data-search-dialog]'), search)) {
      event.preventDefault();
      document.querySelector('[data-search-dialog] input')?.focus();
      return;
    }
    const cart = event.target.closest('[data-open-cart]');
    if (cart && showDialog(document.querySelector('[data-cart-dialog]'), cart)) {
      event.preventDefault();
      return;
    }
    const galleryArrow = event.target.closest('[data-gallery-prev], [data-gallery-next]');
    if (galleryArrow) {
      const gallery = galleryArrow.closest('[data-carousel]');
      if (gallery) {
        event.preventDefault();
        moveGallery(gallery, galleryArrow.hasAttribute('data-gallery-next') ? 1 : -1);
        return;
      }
    }
    const media = event.target.closest('[data-media-link]');
    if (media) {
      const gallery = media.closest('[data-gallery]');
      const target = document.getElementById(media.getAttribute('href').slice(1));
      if (gallery && target && gallery.contains(target)) {
        event.preventDefault();
        setGalleryMedia(gallery, target, true);
      }
    }
    const option = event.target.closest('[data-option-link]');
    if (option) {
      if (selectProductOption(option)) event.preventDefault();
    }
    if (!event.target.closest('.mobile-menu, .nav-dropdown') || event.target.closest('.mobile-menu a')) closeMenus();
    if (event.target instanceof HTMLDialogElement) {
      const bounds = event.target.getBoundingClientRect();
      if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) event.target.close();
    }
  });

  document.addEventListener('keydown', event => {
    if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      const gallery = event.target instanceof Element && event.target.closest('[data-carousel]');
      if (gallery) {
        event.preventDefault();
        moveGallery(gallery, event.key === 'ArrowRight' ? 1 : -1);
        return;
      }
    }
    if (event.key !== 'Escape') return;
    const menu = document.querySelector('.mobile-menu[open], .nav-dropdown[open]');
    if (menu) { closeMenus(); menu.querySelector('summary')?.focus(); }
  });

  document.addEventListener('change', event => {
    if (!(event.target instanceof HTMLSelectElement) || !event.target.matches('[data-native-selling-plan]')) return;
    const selected = event.target.selectedOptions[0];
    if (!selected?.dataset.price) return;
    const price = event.target.closest('[data-product-section]')?.querySelector('[data-product-price]');
    const current = price?.querySelector('[data-price-current]');
    const comparison = price?.querySelector('[data-price-compare]');
    if (current) current.textContent = selected.dataset.price;
    if (comparison) {
      comparison.textContent = selected.dataset.comparePrice || '';
      comparison.hidden = !selected.dataset.comparePrice;
    }
  });

  document.addEventListener('submit', async event => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches('[data-ajax-add]') || !config().cartDrawer) return;
    const dialog = document.querySelector('[data-cart-dialog]');
    if (!dialog || typeof dialog.showModal !== 'function' || !config().cartAddUrl) return;
    const submit = event.submitter || form.querySelector('[name="add"]');
    if (!submit || submit.name !== 'add') return;
    event.preventDefault();
    if (form.dataset.busy || !form.reportValidity()) return;
    form.dataset.busy = 'true';
    form.setAttribute('aria-busy', 'true');
    const error = form.querySelector('[data-form-error]');
    if (error) error.textContent = '';
    const original = submit.textContent;
    submit.disabled = true;
    submit.textContent = config().adding || 'Adding…';
    const body = new FormData(form);
    body.set('sections', 'cart-drawer');
    body.set('sections_url', window.location.pathname);
    let added = false;
    try {
      const response = await fetch(config().cartAddUrl + '.js', {
        method: 'POST', headers: {Accept: 'application/json'}, body, credentials: 'same-origin'
      });
      let data;
      try { data = await response.json(); }
      catch { throw new Error(config().uncertain || 'Please check your cart before trying again.'); }
      if (!response.ok) throw new Error(typeof data.description === 'string' ? data.description : config().error || 'Unable to add this item.');
      added = true;
      const html = data.sections?.['cart-drawer'];
      if (!html) { window.location.assign(config().cartUrl); return; }
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const newDialog = parsed.querySelector('[data-cart-dialog]');
      const oldDialog = document.querySelector('[data-cart-dialog]');
      if (!newDialog || !oldDialog) { window.location.assign(config().cartUrl); return; }
      oldDialog.replaceWith(newDialog);
      initialize(document);
      document.querySelectorAll('[data-cart-count]').forEach(count => {
        if (!count.matches('[data-cart-dialog]')) count.textContent = newDialog.dataset.cartCount || '0';
      });
      showDialog(newDialog, submit);
    } catch (problem) {
      if (added) { window.location.assign(config().cartUrl); return; }
      if (error) error.textContent = problem instanceof TypeError ? (config().uncertain || 'Connection interrupted. Please check your cart before trying again.') : problem.message;
    } finally {
      delete form.dataset.busy;
      form.removeAttribute('aria-busy');
      submit.disabled = false;
      submit.textContent = original;
    }
  });
  initialize();
  document.addEventListener('shopify:section:load', event => initialize(event.target));
})();
